import { useState, useEffect } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Mail, Download, Printer, Copy, Eye, EyeOff } from 'lucide-react';

const UserOnboardingEmails = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      console.log('Fetching users from:', `${API}/users`);
      const response = await api.get(`${API}/users`);
      console.log('Users received:', response.data);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      console.error('Error details:', error.response?.data);
      toast.error(`Failed to load users: ${error.response?.data?.detail || error.message}`);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (userId) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const generateEmailContent = (user) => {
    const password = 'admin123';
    
    return `
Subject: Welcome to C30 Transport Log - Your Account Details

Dear ${user.full_name || user.email},

Welcome to the C30 Transport Log system! Your account has been created successfully.

═══════════════════════════════════════════════
ACCOUNT CREDENTIALS
═══════════════════════════════════════════════

Username/Email: ${user.email}
Temporary Password: ${password}

Login URL: ${window.location.origin}/login

═══════════════════════════════════════════════
IMPORTANT: CHANGE YOUR PASSWORD
═══════════════════════════════════════════════

For security reasons, please change your password immediately after your first login.

HOW TO CHANGE YOUR PASSWORD:

1. Go to ${window.location.origin}/login
2. Log in using the credentials above
3. Click on "Settings" in the navigation menu
4. Go to the "General" tab
5. Click the "Change Password" button
6. Enter your current password and your new password
7. Click "Update Password" to save

PASSWORD REQUIREMENTS:
• Minimum 8 characters
• Use a mix of letters, numbers, and symbols
• Choose a password you haven't used before

═══════════════════════════════════════════════
GETTING STARTED
═══════════════════════════════════════════════

Once logged in, you'll have access to:
• Transport Log - Daily vehicle management

Additional features coming soon

═══════════════════════════════════════════════
NEED HELP?
═══════════════════════════════════════════════

If you have any questions or need assistance or encounter any errors please let me know. We are still testing the system so feedback is essential.

Welcome aboard!

Best regards,
The C30 Transport Log Team

═══════════════════════════════════════════════
This email contains sensitive information. Please do not share your credentials with anyone.
═══════════════════════════════════════════════
    `.trim();
  };

  const downloadEmailContent = (user) => {
    const content = generateEmailContent(user);
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${user.email}_welcome_email.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Email template downloaded');
  };

  const downloadAllEmails = () => {
    const allContent = users.map(user => {
      return `
${'═'.repeat(80)}
USER: ${user.email}
${'═'.repeat(80)}

${generateEmailContent(user)}

`;
    }).join('\n\n');

    const blob = new Blob([allContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all_user_welcome_emails_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('All email templates downloaded');
  };

  const printEmailContent = (user) => {
    const content = generateEmailContent(user);
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Welcome Email - ' + user.email + '</title>');
    printWindow.document.write('<style>body{font-family:monospace;padding:20px;white-space:pre-wrap;}</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(content);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ee7203]"></div>
            <p className="text-lg">Loading users...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <Card className="border-[#ee7203]">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Mail className="w-6 h-6 text-[#ee7203]" />
                User Onboarding Emails
              </CardTitle>
              <CardDescription className="mt-2">
                Generate welcome emails with login credentials for new users. Users should change their password immediately after first login.
              </CardDescription>
            </div>
            <Button
              onClick={downloadAllEmails}
              className="bg-[#ee7203] hover:bg-[#d66502] gap-2"
            >
              <Download className="w-4 h-4" />
              Download All
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Instructions Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <h3 className="font-semibold text-blue-900 mb-2">📋 How to Use</h3>
          <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
            <li>Review each user's credentials below</li>
            <li>Click "View Email" to see the full welcome email template</li>
            <li>Copy the email content or download as a text file</li>
            <li>Send the credentials to each user via your preferred email client</li>
            <li>Remind users to change their password after first login</li>
          </ol>
        </CardContent>
      </Card>

      {/* Password Change Instructions Card */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-4">
          <h3 className="font-semibold text-green-900 mb-2">🔐 Password Change Instructions for Users</h3>
          <div className="text-sm text-green-800 space-y-2">
            <p className="font-medium">After logging in, users can change their password by following these steps:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Click the <strong>Settings</strong> icon in the top navigation</li>
              <li>Go to the <strong>General</strong> tab</li>
              <li>Click the <strong>Change Password</strong> button</li>
              <li>Enter current password and new password</li>
              <li>Click <strong>Update Password</strong></li>
            </ol>
            <p className="mt-2 text-xs italic">Location: Settings → General → Change Password</p>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <div className="space-y-3">
        {users.map((user) => (
          <Card key={user.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ee7203] to-orange-600 flex items-center justify-center text-white font-bold">
                      {(user.email || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{user.full_name || 'No name set'}</h3>
                      <p className="text-sm text-gray-600">{user.email || 'No email'}</p>
                    </div>
                  </div>

                  <div className="ml-13 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Username:</span>
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">{user.email || 'No email'}</code>
                      {user.email && (
                        <button
                          onClick={() => copyToClipboard(user.email, 'Username')}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="Copy username"
                        >
                          <Copy className="w-3 h-3 text-gray-600" />
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Password:</span>
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                        {showPasswords[user.id] 
                          ? (user.password_plain || '[Not available]')
                          : '••••••••'}
                      </code>
                      <button
                        onClick={() => togglePasswordVisibility(user.id)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title={showPasswords[user.id] ? 'Hide password' : 'Show password'}
                      >
                        {showPasswords[user.id] ? (
                          <EyeOff className="w-3 h-3 text-gray-600" />
                        ) : (
                          <Eye className="w-3 h-3 text-gray-600" />
                        )}
                      </button>
                      {user.password_plain && (
                        <button
                          onClick={() => copyToClipboard(user.password_plain, 'Password')}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="Copy password"
                        >
                          <Copy className="w-3 h-3 text-gray-600" />
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Role:</span>
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${
                        user.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                        user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 md:min-w-[200px]">
                  <Button
                    onClick={() => {
                      const content = generateEmailContent(user);
                      copyToClipboard(content, 'Email content');
                    }}
                    variant="outline"
                    className="gap-2 w-full"
                    size="sm"
                  >
                    <Copy className="w-4 h-4" />
                    Copy Email
                  </Button>
                  <Button
                    onClick={() => downloadEmailContent(user)}
                    variant="outline"
                    className="gap-2 w-full"
                    size="sm"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                  <Button
                    onClick={() => printEmailContent(user)}
                    variant="outline"
                    className="gap-2 w-full"
                    size="sm"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {users.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            No users found in the system.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UserOnboardingEmails;
